import { useState } from 'react';
import { Star, Globe } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ProfileAvatar } from '@/components/ui/profile-avatar';
import { cn } from '@/lib/utils';
import { createRating } from '@/services/reputation';
import { useFeedbackTags } from '@/hooks/useFeedbackTags';
import type { PendingRating } from '@/services/pendingRatings';
import { toast } from 'sonner';

interface MandatoryRatingModalProps {
  pendingRating: PendingRating;
  currentIndex: number;
  totalCount: number;
  onComplete: () => void;
}

export default function MandatoryRatingModal({
  pendingRating,
  currentIndex,
  totalCount,
  onComplete,
}: MandatoryRatingModalProps) {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { data: tags = [] } = useFeedbackTags(pendingRating.rating_role, true);

  const isBuyerRating = pendingRating.rating_role === 'buyer_to_seller';
  const title = isBuyerRating ? 'Avaliar Vendedor' : 'Avaliar Comprador';

  const handleTagToggle = (tagKey: string) => {
    setSelectedTags(prev => 
      prev.includes(tagKey)
        ? prev.filter(t => t !== tagKey)
        : [...prev, tagKey]
    );
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Selecione uma nota', { description: 'Por favor, selecione quantas estrelas quer dar.' });
      return;
    }

    setIsSubmitting(true);
    try {
      await createRating({
        negotiation_id: pendingRating.negotiation_id,
        rated_user_id: pendingRating.other_user_id,
        rating,
        comment: comment.trim() || undefined,
        feedback_tags: selectedTags,
        rating_role: pendingRating.rating_role,
        is_public: comment.trim().length > 0, // Automático: comentário = público
      });

      toast('Avaliação enviada!', { description: currentIndex < totalCount 
          ? `Avaliação ${currentIndex} de ${totalCount} concluída.`
          : 'Obrigado pelo seu feedback.' });

      onComplete();
    } catch (error: any) {
      console.error('Error submitting rating:', error);
      toast.error('Erro ao enviar avaliação', { description: error?.message || 'Tente novamente.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayRating = hoveredRating || rating;

  return (
    <Dialog open={true} onOpenChange={() => {/* Cannot close */}}>
      <DialogContent 
        className="sm:max-w-md [&>button]:hidden flex flex-col" 
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle>{title}</DialogTitle>
            {totalCount > 1 && (
              <span className="text-sm text-muted-foreground font-normal">
                {currentIndex} de {totalCount}
              </span>
            )}
          </div>
          <DialogDescription>
            Avalie sua experiência para continuar usando o sistema.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-6 py-4 min-h-0">
          {/* Public visibility notice */}
          <div className="flex items-start gap-2 p-3 rounded-lg bg-primary/5 border border-primary/20">
            <Globe className="h-4 w-4 text-primary shrink-0 mt-0.5" />
            <p className="text-xs text-foreground/90 leading-snug">
              Esta avaliação ficará <span className="font-medium">visível no perfil público</span> de {pendingRating.other_user_name}. Não é uma mensagem para o suporte do ForjaHub.
            </p>
          </div>

          {/* Negotiation info */}
          <div className="p-3 rounded-lg bg-secondary/50 border border-border/50">
            <p className="text-xs text-muted-foreground mb-1">Negociação</p>
            <p className="font-medium text-sm">{pendingRating.listing_title}</p>
          </div>

          {/* User being rated */}
          <div className="flex items-center gap-3">
            <ProfileAvatar src={pendingRating.other_user_avatar} name={pendingRating.other_user_name} className="h-12 w-12" />
            <div>
              <p className="font-medium">{pendingRating.other_user_name}</p>
              <p className="text-sm text-muted-foreground">
                {isBuyerRating ? 'Vendedor' : 'Comprador'}
              </p>
            </div>
          </div>

          {/* Star rating */}
          <div className="space-y-2">
            <Label>Como foi sua experiência?</Label>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onPointerDown={() => setRating(star)}
                  onPointerEnter={() => setHoveredRating(star)}
                  onPointerLeave={() => setHoveredRating(0)}
                  className="p-1 transition-transform hover:scale-110"
                  style={{ touchAction: 'manipulation' }}
                >
                  <Star
                    className={cn(
                      'h-8 w-8 transition-colors pointer-events-none',
                      star <= displayRating
                        ? 'fill-warning text-warning'
                        : 'fill-muted text-muted-foreground'
                    )}
                  />
                </button>
              ))}
              {displayRating > 0 && (
                <span className="ml-2 text-lg font-semibold">
                  {displayRating} {displayRating === 1 ? 'estrela' : 'estrelas'}
                </span>
              )}
            </div>
          </div>

          {/* Feedback tags */}
          {tags.length > 0 && (
            <div className="space-y-3">
              <Label>O que mais gostou? (opcional)</Label>
              <div className="flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <button
                    key={tag.id}
                    type="button"
                    onPointerDown={() => handleTagToggle(tag.tag_key)}
                    className={cn(
                      'flex items-center gap-2 px-3 py-1.5 rounded-full text-sm border transition-colors',
                      selectedTags.includes(tag.tag_key)
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background border-border hover:border-primary/50'
                    )}
                    style={{ touchAction: 'manipulation' }}
                  >
                    {tag.tag_label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Comment */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="comment">Comentário (opcional)</Label>
              <Globe className="h-4 w-4 text-primary" />
            </div>
            <Textarea
              id="comment"
              placeholder="Conte um pouco sobre sua experiência..."
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={500}
              rows={3}
            />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span className="flex items-center gap-1">
                <Globe className="h-3 w-3" />
                Comentários aparecem no perfil público
              </span>
              <span>{comment.length}/500</span>
            </div>
          </div>
        </div>

        <div className="flex justify-end flex-shrink-0 pt-2 border-t border-border/50">
          <Button 
            onClick={handleSubmit} 
            disabled={isSubmitting || rating === 0}
            className="min-w-[140px]"
            style={{ touchAction: 'manipulation' }}
          >
            {isSubmitting ? 'Enviando...' : 'Enviar Avaliação'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
